import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelToken
} from 'axios';
import * as _ from 'lodash';
import Configuration from '../config/Configuration';

import AuthService from '../authentication/services/AuthService';
import AxiosInterceptor from './interceptor/Axios';

import {
  DISPATCHER_ALL_UPLOAD_CANCELED,
  DISPATCHER_UPLOAD_CANCELED,
  MAIN_DISPATCHER_ID,
  MultipleUploadData,
  RequestDataBase,
  RestApiPrefix,
  UploadData,
  UploadFileFormData
} from '../../index';

// <debug>
import { OperationType } from '../../index';
import { getTimeOut } from '../../utility/Util';
import LocalCache from '../../utility/LocalCache';
// </debug>
import utils from 'axios/lib/utils';

type CancelData = {
  cancelToken: CancelToken;
  timeout: number;
  cancelRequest: Function
}

export type MultipleResponseByUploadGroup = {
  data: any[],
  multipleResponses: boolean,
  uploadGroupId: string
}

export default class Ajax {
  readonly ajax: AxiosInstance | null = null;
  // <debug>
  public readonly methods = ['post', 'get', 'put', 'delete', 'head'];
  // </debug>
  public static MERGE_REQUEST_CANCELED_MESSAGE: string = 'MERGE_REQUEST_CANCELED';
  public static cancelMergeRequest: { [key: string]: any[] } = {};

  private baseURL: string = '';
  private downloadFrame = undefined;

  constructor(apiConfig: AxiosRequestConfig) {
    this.ajax = axios.create({
      ...apiConfig,
      paramsSerializer: this.paramsSerializer
    });
    this.baseURL = apiConfig.baseURL as string;
    AxiosInterceptor.intercept(this.ajax as AxiosInstance, this);
  }

  /**
   * The method for encoding characters taken from node_modules (path: puzzle/node_modules/axios/lib/helpers/buildURL.js)
   * @param val
   */
  private encode = val => encodeURIComponent(val)
    .replace(/%40/gi, '@')
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%20/g, '+')

  // TODO After migrating to a new version of Axios, check the functionality of the paramsSerializer method
  /**
   * Build a URL by appending params to the end
   * The method for encoding characters taken from node_modules (path: puzzle/node_modules/axios/lib/helpers/buildURL.js)
   * @param params
   */
  private paramsSerializer = (params:any) => {
    let parts = [];
    const encode = this.encode;
    utils.forEach(params, function serialize(val, key) {
      if (val === null || typeof val === 'undefined') {
        return;
      }

      if (utils.isArray(val)) {
        key = key + '[]';
      } else {
        val = [val];
      }

      utils.forEach(val, function parseValue(v) {
        if (utils.isDate(v)) {
          v = v.toISOString();
        } else if (utils.isObject(v)) {
          v = JSON.stringify(v);
        }

        parts.push(encode(key) + '=' + encode(v));
      });
    });

    return parts.join('&');
  }

  /**
   * onUploadProgress allows handling of progress events for uploads.
   * @param uploadData
   * @param progressEvent Native axios progress upload event.
   */
  private onUploadProgress(uploadData: UploadData, progressEvent: ProgressEvent) {
    const { actionName, cancelRequest, dispatcherId, fileName, uploadGroupId } = uploadData;
    _.assign(progressEvent, {
      actionName,
      fileName,
      uploadGroupId
    });
    this.onDispatcherUploadProgress(progressEvent as any, cancelRequest, dispatcherId);
  }

  /**
   * Will be replaced with dispatcher onDispatcherUploadProgress method
   * @param progressEvent
   * @param cancelRequest
   * @param dispatcherId
   */
  public onDispatcherUploadProgress(progressEvent: ProgressEvent, cancelRequest: Function, dispatcherId: string) {
  }

  // `onDownloadProgress` allows handling of progress events for downloads
  onDownloadProgress(progressEvent: ProgressEvent) {
    // Do whatever you want with the native progress event
    // <debug>
    console.log('onDownloadProgress', progressEvent);
    // </debug>
  }

  /**
   * Prepare request params
   * @param {string} method
   * @param {any} paramOpts
   * @param {any} headers
   */
  prepareParams = (method, paramOpts: {
    params: FormData | any,
    urlParams?: any
  }, headers: any = {}) => {
    let params;
    if (_.includes(['get', 'head', 'delete'], method)) {
      params = {
        params: paramOpts.params || {},
        ...headers
      };
      if (paramOpts.urlParams) {
        params.params.urlParams = paramOpts.urlParams;
      }

      return params;
    } else {
      params = paramOpts.params || {};
      if (paramOpts.urlParams) {
        params.urlParams = paramOpts.urlParams;
      }
      return params;
    }
  }

  /**
   * Convert passed params object into FormData object suitable for server upload.
   * @param params
   */
  private static convertToFormData(params: { [key: string]: any }) {
    return Object.keys(params).reduce((formData: FormData, key: string) => {
      formData.append(key, params[key]);
      return formData;
    }, new FormData());
  }

  public encodeQueryData(queryData: any): string {
    const queries = [];
    for (let param in queryData) {
      if (queryData.hasOwnProperty(param)) {
        queries.push(encodeURIComponent(param) + '=' + encodeURIComponent(queryData[param]));
      }
    }
    return queries.join('&');
  }

  /**
   * Adds additional headers to request.
   * @param headers
   */
  private getAdditionalHeaders = (headers: { [key: string]: string }): AxiosRequestConfig => {
    const { REQUEST_TIMEOUT } = Configuration.get().REST_SERVER;
    return {
      timeout: getTimeOut(REQUEST_TIMEOUT),
      headers
    };
  };

  /**
   * Enables cancellation of request. Used in uploading and downloading request to enable cancel request on
   * demand.
   */
  private makeCancelable = (): CancelData => {
    let cancelRequest: Function = () => {};
    const cancelToken = new axios.CancelToken((c: any) => {
      cancelRequest = c.bind(c);
    });
    return {
      cancelToken,
      timeout: 0,
      cancelRequest
    };
  };

  /**
   * Add all necessary data to upload file requests.
   * @param dispatcherId
   * @param fileName
   * @param additionalConfig
   * @param uploadGroupId
   * @param actionName
   */
  private uploadAdditionalConfig(dispatcherId: string, fileName: string, additionalConfig: any, uploadGroupId?: string, actionName?: string) {
    const cancelableData: CancelData = this.makeCancelable();
    const { cancelRequest } = cancelableData;
    delete cancelableData.cancelRequest;
    _.merge(
      additionalConfig,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: this.onUploadProgress.bind(this, {
          actionName,
          cancelRequest,
          dispatcherId,
          fileName,
          uploadGroupId
        })
      },
      cancelableData
    );
    return additionalConfig;
  }

  /**
   * Add all necessary data to download file requests.
   */
  private downloadAdditionalConfig(isBlobDownload: boolean) {
    return !isBlobDownload ? {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      responseType: 'blob', // 'stream' | 'blob' | 'arraybuffer'
      onDownloadProgress: this.onDownloadProgress.bind(this)
    } : {
      headers: {
        'Access-Control-Expose-Headers': 'Content-Disposition'
      },
      responseType: 'blob'
    };
  }

  /**
   * Initialize all upload files with progresses of 0 before actual upload is started.
   * @param uploadGroupId
   * @param fileNames
   */
  public initializeUploadProgressDataPerGroupUpload(uploadGroupId: string, fileNames: string[]) {
  }

  /**
   * Uploads multiple files at the same time.
   * @param config
   */
  private async multipleConcurrentUpload(config: MultipleUploadData): Promise<any[]> {
    const { actionName, additionalConfig, dispatcherId, method, multipleParams, uploadGroupId, url } = config;
    return Promise.all(
      Object.keys(multipleParams).map(async (fileName: string) => {
        const params = multipleParams[fileName];
        const uploadAdditionalConfigData = this.uploadAdditionalConfig(dispatcherId, fileName, additionalConfig, uploadGroupId, actionName);
        let response;
        try {
          response = await this.ajax[method](url, this.prepareParams(method, {
            params
          }), uploadAdditionalConfigData);
        } catch (e) {
          if ([DISPATCHER_UPLOAD_CANCELED, DISPATCHER_ALL_UPLOAD_CANCELED].indexOf(e?.message) === -1) {
            throw e;
          }
        }
        return response;
      })
    );
  }

  /**
   * Uploads multiple files in sequential order.
   * @param config
   */
  private async multipleSynchronizedUpload(config: MultipleUploadData) {
    const { actionName, additionalConfig, dispatcherId, method, multipleParams, uploadGroupId, url } = config;

    let results: any = {
      errors: [],
      result: []
    };
    let files: string[] = Object.keys(multipleParams);
    let length: number = files.length;
    let index: number = 0;
    let fileName: string;
    let fileSize: number = 0;
    let success;
    let maxUploadSizeExceeded;
    while (index < length) {
      fileName = files[index];
      const params = multipleParams[fileName];
      const uploadAdditionalConfigData = this.uploadAdditionalConfig(dispatcherId, fileName, additionalConfig, uploadGroupId, actionName);
      let response;
      let error;
      let showWarning;
      try {
        response = await this.ajax[method](url, this.prepareParams(method, {
          params
        }), uploadAdditionalConfigData);
        success = _.get(response, 'data.success');
        if (_.isBoolean(success) && !success) {
          maxUploadSizeExceeded = _.get(response, 'data.maxUploadSizeExceeded');
          fileSize = _.get(params.get('file'), 'size');
          showWarning = {
            code: 'UPLOAD_TOO_LARGE',
            hasClose: true
          };
          error = {
            success,
            maxUploadSizeExceeded,
            showWarning,
            fileName,
            uploadGroupId,
            fileSize
          };
          response = null;
        }
      } catch (e) {
        if (e?.message !== DISPATCHER_UPLOAD_CANCELED) {
          fileSize = e?.config?.data.get('file').size;
          error = { ...e, fileName, uploadGroupId, fileSize };
        }
      } finally {
        index++;
      }

      if (response) {
        results.result.push(response);
      } else {
        results.errors.push(error);
      }
    }
    return results;
  }

  /**
   * File downloader.
   * @param result
   */
  private downloadFile = (result: AxiosResponse) => {
    const contentDisposition = _.get(result, 'headers.content-disposition');
    if (_.isString(contentDisposition)) {
      let fileName = contentDisposition.split('filename=')[1];
      if (fileName) {
        fileName = fileName.replace(/"/g, '');
      }
      const link = document.createElement('a');
      const blobObject = new Blob([result.data], {
        // type: 'application/octet-stream'
        type: _.get(result, 'headers.content-type')
      });
      const href = window.URL.createObjectURL(blobObject);
      link.href = href;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(href);
    } else {
      // <debug>
      console.error('File can not be downloaded. Please setup server to expose "Content-Disposition" header.');
      // </debug>
    }
  };

  /**
   * Download file via form submit.
   * @param url
   * @param method
   * @param isAuthRest
   * @param dispatcherId every download will have it's own iframe with id of dispatcherId
   */
  private downloadViaForm = (url: string, method: string = 'get', isAuthRest: boolean = false, dispatcherId: string = MAIN_DISPATCHER_ID) => {
    if (!this.downloadFrame) {
      this.downloadFrame = document.createElement('div');
      document.body.appendChild(this.downloadFrame);
    }
    let frame = document.createElement('iframe');
    const frameId = dispatcherId;
    Object.assign(frame, {
      id: frameId,
      name: frameId,
      style: 'display: none'
    });

    this.downloadFrame.appendChild(frame);

    let form = document.createElement('form');
    Object.assign(form, {
      method,
      encoding: 'multipart/form-data',
      target: frameId,
      style: 'display: none',
      action: this.baseURL + url.replace(/^\//, '')
    });

    frame.appendChild(form);

    const impersonatedUser = LocalCache.getItem('impersonated', 'sessionStorage');
    if (!impersonatedUser) {
      AuthService.getUser().then((user) => {
        let formInput = document.createElement('input');
        let value = Configuration.get().AUTH.authorization;
        if (!isAuthRest && user && user.token_type) {
          value = `${user.token_type[0].toUpperCase() + user.token_type.slice(1)} ${user.access_token}`;
        }

        Object.assign(formInput, {
          type: 'hidden',
          name: 'Authorization',
          value
        });
        form.appendChild(formInput);

        form.submit();

        if (form.parentElement) {
          form.parentElement.removeChild(form);
          form = null;
        }
      }).catch((e) => {});
    } else {
      form.submit();

      if (form.parentElement) {
        form.parentElement.removeChild(form);
        form = null;
      }
    }
  };

  /**
   * Cancels merge request and returns its params.
   * @param requestId
   */
  public cancelMergeRequest = (requestId: string): any => {
    const { url, params, method, subName, cancelRequest } = _.head(Ajax.cancelMergeRequest[requestId]) || {};

    if (_.isFunction(cancelRequest)) {
      cancelRequest(Ajax.MERGE_REQUEST_CANCELED_MESSAGE);
      Ajax.cancelMergeRequest[requestId].shift();
    }
    return {
      data: {
        subName,
        url,
        params,
        method
      }
    };
  };

  public async request(config: RequestDataBase, dispatcherData: any, mappingResponseObjectName: string, actionName: string): Promise<any> {
    let {
      usePrefix = RestApiPrefix.default, // TODO delete after all endpoints prefixes are standardised
      concurrently = false,
      dispatcherId,
      method = 'post',
      uploadGroupId,
      url,
      isRest = true,
      isAuthRest = false, // this is not the real rest, but is used as rest - this is how backend is implemented
      isUpload = false,
      isDownload = false,
      downloadType = 'form',
      withoutResponse = false,
      headers = {},
      handleError,
      mergeRequest = undefined,
      partOfMergeRequest = undefined,
      subName
    } = config;
    let { params = {}, urlParams = null } = dispatcherData;
    let result = null;
    let allResult = null;
    let errors = null;
    let isMergeRequest: boolean = _.isString(mergeRequest) || _.isNumber(mergeRequest);
    let isBlobDownload: boolean = downloadType === 'blob';
    if (isRest) {
      if (isAuthRest) {
        _.assign(params, { isAuthRest });
        params = Ajax.convertToFormData({
          ...params,
          isAuthRest
        });
      } else {
        if (usePrefix === RestApiPrefix.default) {
          const { REST_API_PREFIX } = Configuration.get().REST_SERVER;
          url = `${REST_API_PREFIX}${url}`;
        } else {
          url = `${usePrefix}${url}`;
        }
      }
    }

    // <debug>
    if (!_.includes(this.methods, method)) {
      throw new Error(`"${method}" is not valid method for operation type "${OperationType.request}". Possible method values are ${this.methods.join(', ')}.`);
    }
    if (isUpload && isDownload) {
      throw new Error(`Operation with type "${OperationType.request}" can't have "isUpload" and "isDownload" properties at the same time!`);
    } // </debug>

    let additionalConfig: AxiosRequestConfig = this.getAdditionalHeaders(headers as { [key: string]: string });

    if (isDownload && !isBlobDownload) {
      _.assign(additionalConfig, this.makeCancelable());
    }

    if (partOfMergeRequest) {
      let last = _.last(Ajax.cancelMergeRequest[partOfMergeRequest]);
      if (last) {
        let { cancelableData } = last;
        // assign same token for cancellation to all requests that are part of mergeAsyncRequest operations
        _.assign(additionalConfig, cancelableData);
      }
    } else if (isMergeRequest) {
      let cancelableData: CancelData = this.makeCancelable();
      const { cancelRequest } = cancelableData;
      delete cancelableData.cancelRequest;
      _.assign(additionalConfig, cancelableData);

      if (!Array.isArray(Ajax.cancelMergeRequest[mergeRequest])) {
        Ajax.cancelMergeRequest[mergeRequest] = [];
      }
      Ajax.cancelMergeRequest[mergeRequest].push({
        subName,
        url,
        method,
        params,
        cancelRequest,
        cancelableData
      });
    }

    let isMultipleUpload = Array.isArray(params.file);
    let multipleParams: UploadFileFormData = {};
    if (isUpload) {
      if (isMultipleUpload) {
        multipleParams = params.file.reduce((obj: UploadFileFormData, file) => {
          obj[file.name] = Ajax.convertToFormData({
            ...params,
            file
          });
          return obj;
        }, multipleParams);
      } else {
        let fileName = params.file ? params.file.name : '';
        additionalConfig = this.uploadAdditionalConfig(dispatcherId, fileName, additionalConfig, uploadGroupId);
        params = Ajax.convertToFormData(params);
      }
    }
    if (isDownload) {
      _.merge(additionalConfig, this.downloadAdditionalConfig(isBlobDownload));
    }

    try {
      if (url.match('.json')) {
        const { BASE_ROUTE_NAME, BASE_URL } = Configuration.get();
        url = `${BASE_URL}${BASE_ROUTE_NAME}${url}`;
      }

      if (isMultipleUpload && !_.isEmpty(multipleParams)) {
        const uploadData: MultipleUploadData = {
          actionName,
          multipleParams,
          dispatcherId,
          additionalConfig,
          method,
          uploadGroupId,
          url
        };
        this.initializeUploadProgressDataPerGroupUpload(uploadGroupId, Object.keys(multipleParams));
        if (concurrently) {
          result = await this.multipleConcurrentUpload(uploadData);
        } else {
          allResult = await this.multipleSynchronizedUpload(uploadData);
          result = allResult.result;
          errors = allResult.errors;
        }
      } else {
        if (isDownload && !isBlobDownload) {
          // download file via form submit
          withoutResponse = true;
          this.downloadViaForm(url, method, isAuthRest, dispatcherId);
        } else {
          let prms = this.prepareParams(method, {
            params,
            urlParams
          }, additionalConfig);
          result = await this.ajax[method](url, prms, additionalConfig);
        }
      }

      if (isDownload && isBlobDownload) {
        // download file as blob
        this.downloadFile(result);
        withoutResponse = true;
      }
    } catch (e) {
      if (handleError) {
        result = {
          success: false,
          data: e?.response?.data,
          status: e?.response?.status,
          headers: e?.response?.headers,
          showWarning: e?.showWarning
        };
        if (isMergeRequest) {
          result.isRequestError = true;
          result.message = Ajax.MERGE_REQUEST_CANCELED_MESSAGE;
        }
      } else {
        result = e;
        _.assign(result, {
          isRequestError: true
        });
      }
    }

    if (withoutResponse) {
      result = null;
    }

    if (result) {
      if (Array.isArray(result)) {
        result = {
          data: {
            data: result.filter((data: any) => data).map(({ data }) => data),
            multipleResponses: true,
            uploadGroupId
          } as MultipleResponseByUploadGroup,
          errors
        };
      }
      if (result.data) {
        _.assign(dispatcherData, {
          [mappingResponseObjectName]: handleError
            ? {
              status: result.status,
              headers: result.headers,
              data: result.data
            }
            : result.data
        });
      } else if (result.isRequestError) {
        _.assign(dispatcherData, {
          [mappingResponseObjectName]: result
        });
      }
    }
    return result;
  }
}
